/**
 * @fileoverview Custom hook for managing WebSocket connections in the widget
 * 
 * This hook encapsulates all WebSocket logic for the widget, including:
 * - Connection management with correlationId
 * - Automatic subscription to assistant events
 * - Message handling and forwarding
 * - Automatic reconnection on disconnect
 * - Message sending with correlationId propagation
 * 
 * @module hooks/useWidgetWebSocket
 * @version 1.0.0
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { generateUUID } from '../utils/uuidUtils';

import { createLogger } from '../utils/logger';
const logger = createLogger('hooks');

// Helper for conditional logging (only in development)
const isDevelopment = process.env.NODE_ENV === 'development';
const widgetLog = (...args: any[]) => {
    if (isDevelopment) {
        logger.info(...args);
    }
};
const widgetWarn = (...args: any[]) => {
    if (isDevelopment) {
        logger.warn(...args);
    }
};

export interface UseWidgetWebSocketProps {
    /** WebSocket URL to connect to */
    wsUrl: string;
    /** Chatbot ID for subscription */
    chatbotId: string;
    /** Channel ID (user session identifier) */
    channelId: string;
    /** Callback when a message is received from WebSocket */
    onMessageReceived: (messageData: any) => void;
    /** Callback when connection status changes */
    onConnectionStatusChange?: (status: 'connecting' | 'connected' | 'disconnected' | 'error') => void;
}

export interface UseWidgetWebSocketReturn {
    /** Correlation ID for end-to-end tracing */
    correlationId: string;
    /** Whether subscription to events is confirmed */
    isSubscribed: boolean;
    /** Send a message through WebSocket */
    sendMessage: (payload: any) => boolean;
    /** Manually reconnect to WebSocket */
    reconnect: () => void;
    /** Manually disconnect from WebSocket */
    disconnect: () => void;
    /** Current connection status */
    connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
}

/**
 * Custom hook for managing WebSocket connections in the widget
 * 
 * @param props - Configuration for WebSocket connection
 * @returns WebSocket connection state and methods
 */
export function useWidgetWebSocket({
    wsUrl,
    chatbotId,
    channelId,
    onMessageReceived,
    onConnectionStatusChange
}: UseWidgetWebSocketProps): UseWidgetWebSocketReturn {
    const [correlationId, setCorrelationId] = useState<string>('');
    const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
    const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
    
    const socketRef = useRef<WebSocket | null>(null);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isConnectingRef = useRef<boolean>(false);
    const correlationIdRef = useRef<string>('');
    const onMessageReceivedRef = useRef(onMessageReceived);
    
    // Keep ref updated with latest callback
    useEffect(() => {
        onMessageReceivedRef.current = onMessageReceived;
    }, [onMessageReceived]);

    const onConnectionStatusChangeRef = useRef(onConnectionStatusChange);
    
    // Keep ref updated with latest callback
    useEffect(() => {
        onConnectionStatusChangeRef.current = onConnectionStatusChange;
    }, [onConnectionStatusChange]);
    
    /**
     * Update connection status and notify parent if callback provided
     */
    const updateConnectionStatus = useCallback((status: 'connecting' | 'connected' | 'disconnected' | 'error') => {
        setConnectionStatus(status);
        if (onConnectionStatusChangeRef.current) {
            onConnectionStatusChangeRef.current(status);
        }
    }, []);

    /**
     * Connect to WebSocket server
     */
    const connect = useCallback(() => {
        // Prevent multiple simultaneous connections
        if (isConnectingRef.current || (socketRef.current && socketRef.current.readyState === WebSocket.CONNECTING)) {
            widgetLog('🔌 Widget: WebSocket already connecting, skipping...');
            return;
        }

        // Close existing connections properly
        if (socketRef.current) {
            socketRef.current.onclose = null; // Prevent reconnection from old socket
            socketRef.current.onerror = null;
            socketRef.current.onmessage = null;
            socketRef.current.onopen = null;
            if (socketRef.current.readyState !== WebSocket.CLOSED) {
                socketRef.current.close();
            }
        }

        // Clear any pending reconnection attempts
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        // 🆔 Generate correlationId for end-to-end tracing
        const newCorrelationId = generateUUID();
        setCorrelationId(newCorrelationId);
        correlationIdRef.current = newCorrelationId;
        setIsSubscribed(false);
        isConnectingRef.current = true;

        // Build connection URL with parameters
        const urlWithParams = `${wsUrl}?chatbotId=${chatbotId}&channelId=${channelId}&correlationId=${newCorrelationId}`;
        
        widgetLog('🔌 Widget: Connecting to WebSocket', {
            correlationId: newCorrelationId,
            chatbotId,
            channelId,
            url: urlWithParams
        });

        updateConnectionStatus('connecting');

        const socket = new WebSocket(urlWithParams);

        socket.onopen = () => {
            widgetLog('✅ Widget: WebSocket Connected', { correlationId: newCorrelationId });
            isConnectingRef.current = false;
            updateConnectionStatus('connected');

            // Clear any reconnection attempts on successful connection
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }

            // 📡 Subscribe to assistant-level events
            const subscriptionMessage = {
                action: 'subscribe',
                resourceType: 'assistant',
                resourceId: chatbotId,
                eventTypes: ['message.*', 'conversation.*']
            };
            
            widgetLog('📡 Widget: Sending subscription request', {
                correlationId: newCorrelationId,
                subscription: subscriptionMessage
            });
            
            socket.send(JSON.stringify(subscriptionMessage));
        };

        socket.onmessage = (e) => {
            try {
                const messageData = JSON.parse(e.data);
                const currentCorrelationId = correlationIdRef.current;

                if (!messageData) {
                    widgetWarn('⚠️ Widget: Received null or undefined message data', {
                        correlationId: currentCorrelationId
                    });
                    return;
                }

                // Handle 'connected' event (server acknowledgment)
                if (messageData.event === 'connected') {
                    widgetLog('✅ Widget: Connection acknowledged by server', {
                        correlationId: currentCorrelationId
                    });
                    return;
                }

                // Handle subscription response
                if (messageData.event === 'subscription:response') {
                    const subscriptionData = messageData.data || messageData;
                    const isSuccess = subscriptionData.success === true;
                    
                    setIsSubscribed(isSuccess);
                    
                    widgetLog('✅ Widget: Subscription response received', {
                        correlationId: currentCorrelationId,
                        success: isSuccess,
                        resourceType: subscriptionData.details?.resourceType,
                        resourceId: subscriptionData.details?.resourceId,
                        eventTypes: subscriptionData.details?.eventTypes,
                        message: subscriptionData.message
                    });
                    
                    if (!isSuccess) {
                        logger.error('❌ Widget: Subscription failed', {
                            correlationId: currentCorrelationId,
                            error: subscriptionData.error || subscriptionData.message
                        });
                    }
                    
                    return;
                }

                // Forward all other messages to parent component for processing
                onMessageReceivedRef.current(messageData);
                
            } catch (error) {
                const currentCorrelationId = correlationIdRef.current;
                logger.error('❌ Widget: Error parsing message', {
                    correlationId: currentCorrelationId,
                    error: error instanceof Error ? error.message : String(error)
                });
            }
        };

        socket.onclose = (e) => {
            const currentCorrelationId = correlationIdRef.current;
            isConnectingRef.current = false;
            
            widgetLog('🔌 Widget: WebSocket connection closed', {
                correlationId: currentCorrelationId,
                code: e.code,
                reason: e.reason
            });

            updateConnectionStatus('disconnected');
            setIsSubscribed(false);

            // Only attempt reconnection if it wasn't a deliberate close (code 1000)
            if (e.code !== 1000 && socketRef.current === socket) {
                widgetLog('🔄 Widget: Connection lost unexpectedly, scheduling reconnection...', {
                    correlationId: currentCorrelationId
                });
                
                reconnectTimeoutRef.current = setTimeout(() => {
                    widgetLog('🔄 Widget: Attempting to reconnect WebSocket...', {
                        correlationId: currentCorrelationId
                    });
                    connect();
                }, 5000);
            } else {
                widgetLog('🔌 Widget: WebSocket closed deliberately, no reconnection scheduled', {
                    correlationId: currentCorrelationId
                });
            }
        };

        socket.onerror = (error) => {
            const currentCorrelationId = correlationIdRef.current;
            isConnectingRef.current = false;
            
            logger.error('❌ Widget: WebSocket Error', {
                correlationId: currentCorrelationId,
                error
            });
            
            updateConnectionStatus('error');
            // Don't immediately reconnect on error, let onclose handle it
        };

        socketRef.current = socket;
    }, [wsUrl, chatbotId, channelId]);

    /**
     * Manually disconnect from WebSocket
     */
    const disconnect = useCallback(() => {
        if (socketRef.current) {
            // Prevent reconnection
            if (socketRef.current.onclose) {
                socketRef.current.onclose = null;
            }
            socketRef.current.close(1000, 'User initiated disconnect');
            socketRef.current = null;
        }
        
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        isConnectingRef.current = false;
        setIsSubscribed(false);
        updateConnectionStatus('disconnected');
        
        widgetLog('🔌 Widget: WebSocket disconnected by user', {
            correlationId
        });
    }, [correlationId, updateConnectionStatus]);

    /**
     * Send a message through WebSocket
     * 
     * @param payload - Message payload to send
     * @returns true if message was sent, false otherwise
     */
    const sendMessage = useCallback((payload: any): boolean => {
        const currentCorrelationId = correlationId;
        
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            const messageWithCorrelationId = {
                ...payload,
                correlationId: currentCorrelationId // 🆔 Always include correlationId
            };
            
            socketRef.current.send(JSON.stringify(messageWithCorrelationId));
            
            widgetLog('📤 Widget: Message sent', {
                correlationId: currentCorrelationId,
                action: payload.action
            });
            
            return true;
        }
        
        widgetWarn('⚠️ Widget: WebSocket not connected, message not sent', {
            correlationId: currentCorrelationId,
            readyState: socketRef.current?.readyState,
            action: payload.action
        });
        
        return false;
    }, [correlationId]);

    // Track previous values to avoid unnecessary reconnections
    const prevParamsRef = useRef<{ wsUrl: string; chatbotId: string; channelId: string } | null>(null);
    
    // Auto-connect on mount and when dependencies change
    useEffect(() => {
        if (!wsUrl || !chatbotId || !channelId) {
            widgetWarn('⚠️ Widget: Missing required WebSocket parameters', {
                wsUrl: !!wsUrl,
                chatbotId: !!chatbotId,
                channelId: !!channelId
            });
            return;
        }

        // Check if params actually changed
        const currentParams = { wsUrl, chatbotId, channelId };
        const prevParams = prevParamsRef.current;
        
        if (prevParams && 
            prevParams.wsUrl === currentParams.wsUrl &&
            prevParams.chatbotId === currentParams.chatbotId &&
            prevParams.channelId === currentParams.channelId) {
            // Params haven't changed, don't reconnect
            return;
        }
        
        // Update ref with current params
        prevParamsRef.current = currentParams;

        // Connect only if not already connecting or connected
        if (!isConnectingRef.current && (!socketRef.current || socketRef.current.readyState === WebSocket.CLOSED)) {
            connect();
        }

        // Cleanup on unmount or when dependencies change
        return () => {
            // Only disconnect if we're actually connected
            if (socketRef.current && socketRef.current.readyState !== WebSocket.CLOSED) {
                // Prevent reconnection by clearing the close handler
                if (socketRef.current.onclose) {
                    socketRef.current.onclose = null;
                }
                socketRef.current.close(1000, 'Dependencies changed');
                socketRef.current = null;
            }
            
            // Clear any pending reconnection attempts
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            
            isConnectingRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wsUrl, chatbotId, channelId]);

    return {
        correlationId,
        isSubscribed,
        sendMessage,
        reconnect: connect,
        disconnect,
        connectionStatus
    };
}

